import { useId, type ReactNode } from 'react'

type WaveProps = {
  /** Width and height of the artwork in its own units. */
  width: number
  height: number
  /** The artwork to wave, drawn from 0,0 to width,height. */
  children: ReactNode
  label?: string
  className?: string
}

const SLICES = 24

/**
 * Waves any rectangular artwork like cloth. The art is cut into vertical
 * slices; each slice rises, falls and darkens a beat after its neighbor, so a
 * wave travels from the hoist to the fly. The amplitude grows toward the fly,
 * the way a flag pinned at one edge moves. Reduced motion holds it still.
 */
export default function Wave({ width, height, children, label, className }: WaveProps) {
  const id = useId().replace(/:/g, '')
  const slice = width / SLICES
  const pad = height * 0.12
  return (
    <svg
      viewBox={`0 ${-pad} ${width} ${height + pad * 2}`}
      className={`mds-wave${className ? ` ${className}` : ''}`}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ '--wave-amp': `${height * 0.075}px` } as React.CSSProperties}
    >
      <defs>
        <g id={`${id}-art`}>{children}</g>
        {Array.from({ length: SLICES }, (_, i) => (
          <clipPath key={i} id={`${id}-c${i}`}>
            <rect x={i * slice - 0.25} y={-pad} width={slice + 0.5} height={height + pad * 2} />
          </clipPath>
        ))}
      </defs>
      {Array.from({ length: SLICES }, (_, i) => {
        const reach = 0.25 + (i / (SLICES - 1)) * 0.75
        const style = {
          animationDelay: `${-i * 70}ms`,
          '--wave-reach': reach.toFixed(3),
        } as React.CSSProperties
        return (
          <g key={i} clipPath={`url(#${id}-c${i})`}>
            <g className="mds-wave__slice" style={style}>
              <use href={`#${id}-art`} />
              <rect
                className="mds-wave__shade"
                x={i * slice - 0.25}
                y={0}
                width={slice + 0.5}
                height={height}
                style={{ animationDelay: `${-i * 70}ms` }}
              />
            </g>
          </g>
        )
      })}
    </svg>
  )
}
